'use client';

/**
 * ProgramFieldOverridesManager
 *
 * Phase 0 of the per-program field configuration roadmap. The organization
 * owns one base field library (configured in the Profile Builder). This panel
 * lets an admin layer per-program overrides on top of that base:
 *   - hide a field for a specific program (is_visible=false)
 *   - mark a field required for a specific program
 *
 * It writes to program_field_visibility via the new
 * /api/member-field-config/programs/{id}/field-overrides endpoints, which
 * get_member_profile_fields() already consumes when rendering a member's
 * profile in a program context. The org base is never mutated here.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { membersAPI, ProfileField, ProgramFieldOverride } from '@/services/api/members';
import { programsAPI, Program } from '@/services/api/programs';

// listPrograms can return cross-org programs for super_admins; scope to the
// active cbo (the X-CBO-ID the API is called with) so per-org features don't
// list — and then 404 on — programs owned by another org.
function filterToActiveCbo(programs: Program[]): Program[] {
  const active = Number(localStorage.getItem('active_cbo_id'));
  return active ? programs.filter((p) => p.cbo_id === active) : programs;
}

// The effective per-program state for one base field, merged from the override
// row (if present) over the org base.
interface RowState {
  field: ProfileField;
  hasOverride: boolean;
  visible: boolean;
  required: boolean;
  dirty: boolean;
}

function buildRows(
  base: ProfileField[],
  overrides: ProgramFieldOverride[]
): RowState[] {
  const byKey = new Map<string, ProgramFieldOverride>();
  overrides.forEach((o) => byKey.set(`${o.is_core_field ? 'c' : 'x'}:${o.field_key}`, o));
  return base.map((f) => {
    const ov = byKey.get(`${f.is_core_field ? 'c' : 'x'}:${f.field_key}`);
    return {
      field: f,
      hasOverride: !!ov,
      // base fields come back with is_hidden already applied; an override of
      // is_visible=false wins. Default (no override) follows the base.
      visible: ov ? !!ov.is_visible : !f.is_hidden,
      required: ov && ov.is_required != null ? !!ov.is_required : !!f.is_required,
      dirty: false,
    };
  });
}

export default function ProgramFieldOverridesManager() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programId, setProgramId] = useState<number | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    programsAPI
      .listPrograms({ is_active: true })
      // Per-program overrides are scoped to the active org's field config, so
      // only that org's programs are valid here. listPrograms can return
      // cross-org programs for super_admins; filtering avoids 404s on programs
      // owned by another cbo.
      .then((p) => setPrograms(filterToActiveCbo(p)))
      .catch((e) => setError(e?.message || 'Failed to load programs'));
  }, []);

  const loadOverrides = useCallback(async (pid: number) => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      // include_hidden so admins can also re-show a field that the org hides
      // by default for a specific program.
      const [base, overrides] = await Promise.all([
        membersAPI.getProfileFields({ include_hidden: true }),
        membersAPI.listProgramFieldOverrides(pid),
      ]);
      setRows(buildRows(base, overrides));
    } catch (e: any) {
      setError(e?.message || 'Failed to load field overrides');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (programId != null) loadOverrides(programId);
  }, [programId, loadOverrides]);

  const dirtyCount = useMemo(() => rows.filter((r) => r.dirty).length, [rows]);

  const update = (key: string, isCore: boolean, patch: Partial<RowState>) => {
    setRows((prev) =>
      prev.map((r) =>
        r.field.field_key === key && r.field.is_core_field === isCore
          ? { ...r, ...patch, dirty: true }
          : r
      )
    );
  };

  const save = async () => {
    if (programId == null) return;
    const changed = rows.filter((r) => r.dirty);
    if (changed.length === 0) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload: ProgramFieldOverride[] = changed.map((r) => ({
        field_key: r.field.field_key,
        is_core_field: r.field.is_core_field,
        is_visible: r.visible,
        is_required: r.required,
        // sort_order is left to the org base in Phase 0; per-program reorder
        // is a later increment.
        sort_order: null,
      }));
      const res = await membersAPI.setProgramFieldOverrides(programId, payload);
      if (res.errors?.length) {
        setError(`${res.errors.length} field(s) failed: ${res.errors[0]}`);
      } else {
        setMessage(`Saved ${res.applied} override(s).`);
      }
      await loadOverrides(programId);
    } catch (e: any) {
      setError(e?.message || 'Failed to save overrides');
    } finally {
      setSaving(false);
    }
  };

  const resetField = async (r: RowState) => {
    if (programId == null || !r.hasOverride) return;
    try {
      await membersAPI.deleteProgramFieldOverride(
        programId,
        r.field.field_key,
        r.field.is_core_field
      );
      await loadOverrides(programId);
    } catch (e: any) {
      setError(e?.message || 'Failed to reset field');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Program Field Overrides</CardTitle>
        <p className="text-sm text-muted-foreground">
          Pick a program, then hide fields or mark them required for that program
          only. The organization-wide setup in Profile Builder stays the source of
          truth; these overrides layer on top.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-72">
            <Select
              value={programId != null ? String(programId) : ''}
              onValueChange={(v) => setProgramId(v ? Number(v) : null)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a program…" />
              </SelectTrigger>
              <SelectContent>
                {programs.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {programId != null && (
            <Button onClick={save} disabled={saving || dirtyCount === 0}>
              {saving ? 'Saving…' : `Save${dirtyCount ? ` (${dirtyCount})` : ''}`}
            </Button>
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {message && <p className="text-sm text-green-600">{message}</p>}

        {programId == null ? (
          <p className="text-sm text-muted-foreground">
            Select a program to view and edit its field overrides.
          </p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground">Loading fields…</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Field</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="w-28 text-center">Visible</TableHead>
                <TableHead className="w-28 text-center">Required</TableHead>
                <TableHead className="w-24 text-center">Override</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.field.is_core_field ? 'c' : 'x'}:${r.field.field_key}`}>
                  <TableCell>
                    <div className="font-medium">{r.field.display_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.field.field_key}
                      {r.field.is_core_field ? ' · core' : ' · custom'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {r.field.category_name}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={r.visible}
                      onCheckedChange={(checked: boolean) =>
                        update(r.field.field_key, r.field.is_core_field, { visible: checked })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={r.required}
                      onCheckedChange={(checked: boolean) =>
                        update(r.field.field_key, r.field.is_core_field, { required: checked })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    {r.hasOverride ? (
                      <button
                        className="text-xs text-blue-600 underline"
                        onClick={() => resetField(r)}
                        type="button"
                      >
                        Reset
                      </button>
                    ) : (
                      <Badge variant="outline">base</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
